import { Workflow, WorkflowNode, WorkflowNodeType } from 'api/model';
import { getAllNodesKeys, getNodeConfig } from 'api/workflows-steps/formSchema';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ReactFlow, Controls, Edge, Node, ReactFlowInstance } from 'reactflow';
import CustomEdge from 'views/flow/canvas/edges/CustomEdge';
import FallbackNode from 'views/flow/canvas/nodes/FallbackNode';
import FormNode from 'views/flow/canvas/nodes/FormNode';
import FormProxyNode from 'views/flow/canvas/nodes/FormProxyNode';
import { NodeTypes } from '../../types';
import { PreviewContainer } from './PreviewWorkflow.styles';

export interface PreviewWorkflowProps {
  workflow?: Workflow;
  selected?: string;
  onChange: (nodeId: string) => void;
}

export default function PreviewWorkflow({ workflow, selected, onChange }: PreviewWorkflowProps) {
  const selectedWorkflow = workflow;
  const boxOffsetX = 250.0;
  const nodeWidth = 90.0;
  const boxMargin = 50.0;

  const calculateBoundingBox = useCallback((nodes: Node[]) => {
    if (nodes.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const node of nodes) {
      if (node.position.x < minX) minX = node.position.x;
      if (node.position.x > maxX) maxX = node.position.x;
      if (node.position.y < minY) minY = node.position.y;
      if (node.position.y > maxY) maxY = node.position.y;
    }

    return {
      x: minX - boxMargin,
      y: minY - boxMargin,
      width: maxX - minX + nodeWidth + boxMargin * 2,
      height: maxY - minY + nodeWidth + boxMargin * 2,
    };
  }, []);

  const stepsConfig = getAllNodesKeys();

  const nodeTypes: NodeTypes = useMemo((): NodeTypes => {
    const types = stepsConfig.reduce(
      (obj, key: string) => ({
        ...obj,
        [key]: FormNode,
        proxy: FormProxyNode,
      }),
      {},
    ) as NodeTypes;
    return { ...types, ...{ default: FallbackNode } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const edgeTypes = useMemo(() => ({ default: CustomEdge }), []);

  const nodes = useMemo(() => {
    if (!selectedWorkflow) return [];

    const nodes =
      selectedWorkflow.nodes.map((node) => {
        const nodeType = getNodeConfig(node.type);
        return {
          id: node.id,
          type: node.type,
          data: { ...node, source: nodeType?.source, target: nodeType?.target },
          position: node.viewport_metadata?.position ?? { x: 0.0, y: 0.0 },
          selected: node.id == selected,
          style: {
            ...(node.id == selected
              ? {
                  borderRadius: '22.5%',
                  boxShadow: '0 0 0 3px #4C60C9, 0 0 14px 2px rgba(76, 96, 201, 0.45)',
                  transition: 'box-shadow 160ms ease',
                }
              : undefined),
          },
        } as Node<WorkflowNode, WorkflowNodeType>;
      }) ?? [];

    const bbox = calculateBoundingBox(nodes);
    const xOffset = bbox.x - boxOffsetX;
    const yOffset = bbox.y;
    return nodes.map((node) => {
      return {
        ...node,
        style: node.style,
        position: { x: node.position.x - xOffset, y: node.position.y - yOffset },
      };
    });
  }, [selectedWorkflow, selected, calculateBoundingBox]);

  const edges = useMemo(() => {
    if (!selectedWorkflow) return [];

    return (
      selectedWorkflow.edges.map((edge) => {
        return { ...edge, deletable: false } as Edge;
      }) ?? []
    );
  }, [selectedWorkflow]);

  const rfRef = useRef<ReactFlowInstance | null>(null);
  const onInit = useCallback((inst: ReactFlowInstance) => {
    rfRef.current = inst;
    inst.fitView({ padding: 0.15, includeHiddenNodes: true });
  }, []);

  useEffect(() => {
    if (rfRef.current && nodes.length && !selected) {
      rfRef.current.fitView({ padding: 0.15, includeHiddenNodes: true });
    }
  }, [nodes.length, edges.length, selected]);

  const handleNodeClick = useCallback(
    (_e: React.MouseEvent, n: Node) => {
      onChange(n.id);
    },
    [onChange],
  );

  const handlePaneClick = useCallback(() => {
    onChange('');
  }, [onChange]);

  return (
    <div className="w-full h-72" style={{ flexGrow: 1, display: 'flex' }}>
      <PreviewContainer className="relative w-full h-full">
        <ReactFlow
          onInit={onInit}
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          zoomOnScroll
          zoomOnPinch
          zoomOnDoubleClick
          panOnScroll
          panOnDrag
          selectionOnDrag={false}
          nodesDraggable={false}
          nodesConnectable={false}
          onNodeClick={handleNodeClick}
          onPaneClick={handlePaneClick}
          style={{ width: '100%', height: '100%' }}
          proOptions={{ hideAttribution: true }}
        >
          <Controls className="preview-flow-controls" position="bottom-right" showInteractive={false} />
        </ReactFlow>
      </PreviewContainer>
    </div>
  );
}
